import { LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { httpMixin } from './http-mixin';

// Define types
interface ServiceResponse {
  parseAll(config: any): void;
}

interface ParserConfig {
  [key: string]: any;
}

export interface ServiceCallParams {
  [key: string]: any;
}

// Helper type for constructor functions
type Constructor<T = LitElement> = new (...args: any[]) => T;

/**
 * Abstract Service Call Mixin for making API calls with loading states
 * Must be extended by concrete implementations
 */
export const serviceCallMixin = <T extends Constructor<LitElement>>(BaseClass: T) => {
  abstract class ServiceCallMixin extends httpMixin(BaseClass) {
    /**
     * Whether the service is currently loading
     */
    @property({ type: Boolean })
    loading = false;

    /**
     * The service endpoint
     */
    @property({ type: String })
    service?: string;

    constructor(...args: any[]) {
      super(...args);
      this.loading = false;
    }

    /**
     * Lifecycle method called when the element is first updated
     */
    

    /**
     * Get service call parameters - must be implemented by subclasses
     */
    abstract serviceCallParams(): ServiceCallParams;

    /**
     * Get parser configuration
     * @returns {ParserConfig} Parser configuration object
     */
    protected parserConfig(): ParserConfig {
      return {};
    }

    /**
     * Call service with specific parameters
     * @param service - The service endpoint
     * @param params - The parameters to send
     * @returns Promise that resolves with the service response
     */
    protected async callServiceParams(service: string, params: ServiceCallParams): Promise<any> {
      this.loading = true;
      
      try {
        const response: ServiceResponse = await this.call(service, params);
        response.parseAll(this.parserConfig());
        this.loading = false;
        return response;
      } catch (error) {
        this.loading = false;
        console.error('Service call failed:', error);
        throw error;
      }
    }

    /**
     * Call service with parameters from serviceCallParams implementation
     * @param service - The service endpoint (optional, uses this.service if not provided)
     * @returns Promise that resolves with the service response
     */
    protected async callService(service?: string): Promise<any> {
      const serviceEndpoint = service || this.service;
      if (!serviceEndpoint) {
        throw new Error('Service endpoint is required. Either provide a service parameter or set the service property.');
      }
      
      const params = this.serviceCallParams();
      return this.callServiceParams(serviceEndpoint, params);
    }

    /**
     * Handle service call errors
     * @param error - The error that occurred
     */
    protected handleServiceError(error: any): void {
      console.error('Service call error:', error);
      // Can be overridden by subclasses for custom error handling
    }
  }

  return ServiceCallMixin as T & Constructor<ServiceCallMixin>;
};